import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { computeText } from '@/locales/utils';
import { StepData } from '@/components/atoms/Wizard/Wizard';
import { stringifyError } from '@/utils/errors';
import {
    useLazyGetMenuByIdQuery,
    useImportMenuFromExcelMutation,
    usePostMenuMutation,
    usePutMenuByIdMutation,
} from '@/services/innovorder/menu/menu.endpoints';
import type { MenuChannel, MenuPosDevice, PostImportMenuFromExcelResponse } from '@/services/innovorder/menu/menu.type';
import { getUserToken } from '@/redux/user';
import { useGetBrandQuery } from '@/services/innovorder/brand/brand.endpoints';
import { useGetExternalIdsQuery } from '@/services/innovorder/externalIds/externalIds.endpoints';
import { ChannelId } from '@/services/innovorder/channel/channel.type';
import { useDeactivateBannersOnMenuChange } from '@/hooks/useDeactivateBannersOnMenuChange';
import type { PromotionalBanner } from '@/services/innovorder/promotionalBanner/promotionalBanner.type';
import { ImportMenuStep } from './ImportMenuStep';
import { EditChannelStep } from './EditChannelStep/EditChannelStep';
import { SelectPosDeviceStep } from './SelectPosDeviceStep/SelectPosDeviceStep';
import { EditChannelFormValues } from './CreateMenuModal.type';
import { SelectMarketplaceStoreStep } from './SelectMarketplaceStoreStep/SelectMarketplaceStoreStep';

type CreateMenuModalVMProps = {
    onHide: () => void;
    brandId: number;
    restaurantId?: number;
    menuId: number | null;
    channels: MenuChannel[];
    posDevices: MenuPosDevice[];
    marketplaceStores: string[];
    menuName: string | null;
    isSyncFromPosEnabled: boolean;
};

type OnStepChange = () => boolean;

export const useCreateMenuModalVM = ({
    onHide,
    brandId,
    restaurantId,
    menuId,
    channels,
    posDevices,
    marketplaceStores,
    menuName,
    isSyncFromPosEnabled,
}: CreateMenuModalVMProps) => {
    const [currentStep, setCurrentStep] = useState(0);
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [isImportProcessLoading, setImportProcessLoading] = useState(false);
    const [errorMessageChannels, setErrorMessageChannels] = useState('');
    const [disableSyncFromPos, setDisableSyncFromPos] = useState(false);
    const { getActiveBanners, deactivateAndNotify } = useDeactivateBannersOnMenuChange();
    const brandResponse = useGetBrandQuery({ brandId: Number(brandId) });
    const hasExternalIdMapping = Boolean(brandResponse.data?.options?.EXTERNAL_IDS_MAPPING?.isActivated);
    const isLinkingMenuToPosAndWebEnabled = Boolean(
        brandResponse.data?.options?.ENABLE_LINKING_MENU_TO_POS_AND_WEB?.isActivated,
    );
    const { data: dataExternalIds, refetch: refetchExternalIds } = useGetExternalIdsQuery(
        {
            mappingKey: 'menuId',
            internalValues: menuId?.toString() ?? '',
            token,
        },
        { skip: !menuId },
    );

    const [getMenuById, { data: menu }] = useLazyGetMenuByIdQuery();

    useEffect(() => {
        if (menuId) {
            getMenuById({ menuId });
        }
    }, [menuId, getMenuById]);

    const [putMenuById, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess, error: errorPut }] =
        usePutMenuByIdMutation();
    const [postMenu, { isLoading: isCreateLoading, isSuccess: isCreateSuccess, error: errorPost }] =
        usePostMenuMutation();
    const [importMenuFromExcel, { isLoading: isImportLoading, isSuccess: isImportSuccess, error: errorImport }] =
        useImportMenuFromExcelMutation();
    const isSuccess = isCreateSuccess || isUpdateSuccess || isImportSuccess;
    const isLoading = isCreateLoading || isUpdateLoading || isImportLoading || isImportProcessLoading;
    const error = errorPut || errorPost || errorImport;

    const errorMessage = React.useMemo(() => {
        if (error && 'status' in error) {
            const errorData = error.data as { extraData?: { errors?: { lineNumber: string; error: string }[] } };
            const extraData = errorData?.extraData
                ? errorData.extraData?.errors
                      ?.map(({ error: lineError, lineNumber }) => `${lineNumber}: ${lineError}`)
                      .join('\n')
                : '';
            return `${stringifyError(error)}${extraData ? `\n${extraData}` : ''}`;
        }
        return '';
    }, [error]);

    const form: UseFormReturn<EditChannelFormValues> = useForm<EditChannelFormValues>({
        mode: 'all',
        defaultValues: {
            name: menuName,
            file: null,
            isSyncFromPosEnabled,
            channels,
            posDevices,
            marketplaceStores,
        },
    });

    const jumpToStep =
        (step: number): OnStepChange =>
        () => {
            setCurrentStep(step);
            // since we check the return type of this function, we need to return false to avoid the wizard from calling the next or previous step, since we want to jump to the step directly
            return false;
        };

    const handleStepChange =
        (type: 'next' | 'previous'): OnStepChange =>
        () => {
            setCurrentStep(type === 'previous' ? currentStep - 1 : currentStep + 1);
            return true;
        };

    const handleHide = useCallback(() => {
        if (!isLoading) {
            onHide();
        }
        return true;
    }, [isLoading, onHide]);

    const editMenuStepIndex = useMemo(() => {
        return menuId ? 0 : 1;
    }, [menuId]);

    const steps: StepData[] = [
        {
            title: `menu.modal.channels.${menuId ? 'editMenu' : 'createMenu'}`,
            component: (
                <EditChannelStep
                    onNext={handleStepChange('next')}
                    openDeliverooModal={jumpToStep(editMenuStepIndex + 2)}
                    openUbereatsModal={jumpToStep(editMenuStepIndex + 3)}
                    restaurantId={restaurantId}
                    isLoading={isLoading}
                    hasExternalIdMapping={hasExternalIdMapping}
                    menu={menu}
                />
            ),
            showHeader: true,
            showNext: false,
            showValidate: true,
            onCancel: handleHide,
            onNext: handleStepChange('next'),
            onPrevious: handleStepChange('previous'),
        },
        ...(restaurantId
            ? [
                  {
                      title: 'menu.modal.channels.selectPos',
                      component: (
                          <SelectPosDeviceStep
                              brandId={brandId}
                              restaurantId={restaurantId}
                              disableSyncFromPos={disableSyncFromPos}
                          />
                      ),
                      showHeader: true,
                      showNext: true,
                      showPrevious: true,
                      showValidate: false,
                      nextLabel: computeText(intl, 'button.validate'),
                      onNext: jumpToStep(editMenuStepIndex),
                      onPrevious: jumpToStep(editMenuStepIndex),
                  },
                  {
                      title: 'menu.modal.channels.selectDeliveroo',
                      component: <SelectMarketplaceStoreStep marketplace="deliveroo" />,
                      showHeader: true,
                      showNext: true,
                      showPrevious: true,
                      showValidate: false,
                      nextLabel: computeText(intl, 'button.validate'),
                      onNext: jumpToStep(editMenuStepIndex),
                      onPrevious: jumpToStep(editMenuStepIndex),
                  },
                  {
                      title: 'menu.modal.channels.selectUberEats',
                      component: <SelectMarketplaceStoreStep marketplace="ubereats" />,
                      showHeader: true,
                      showNext: true,
                      showPrevious: true,
                      showValidate: false,
                      nextLabel: computeText(intl, 'button.validate'),
                      onNext: jumpToStep(editMenuStepIndex),
                      onPrevious: jumpToStep(editMenuStepIndex),
                  },
              ]
            : []),
    ];

    React.useEffect(() => {
        form.watch(['file', 'sourcePosId', 'posDevices', 'channels']);
    }, [form]);

    if (!menuId) {
        const hasData = form.getFieldState('file')?.isDirty || form.getFieldState('sourcePosId')?.isDirty;
        steps.unshift({
            title: 'menu.modal.channels.createMenu',
            component: <ImportMenuStep brandId={brandId} restaurantId={restaurantId} />,
            showHeader: false,
            nextLabel: computeText(intl, hasData ? 'button.validate' : 'button.skipStep'),
            onNext: handleStepChange('next'),
            onPrevious: handleHide,
        });
    }

    const watchedPosDevices = form.watch('posDevices');
    const watchedChannels = form.watch('channels');

    React.useEffect(() => {
        const formPosDevices = watchedPosDevices ?? [];
        const formChannels = watchedChannels ?? [];
        if (
            formPosDevices.length > 0 &&
            formChannels.length > 0 &&
            !isSyncFromPosEnabled &&
            !isLinkingMenuToPosAndWebEnabled
        ) {
            setDisableSyncFromPos(true);
        } else {
            setDisableSyncFromPos(false);
        }
    }, [watchedPosDevices, watchedChannels, isSyncFromPosEnabled, isLinkingMenuToPosAndWebEnabled, form]);

    const onFormValidate = async () => {
        const { getValues, setError } = form;
        const {
            sourcePosId,
            name,
            channels: formChannels,
            posDevices: formPosDevices,
            marketplaceStores: formMarketplaceStores,
            file,
            isSyncFromPosEnabled: formIsPosSyncActive,
            externalId,
            imageId,
        } = getValues();
        const channelIds = formChannels.map(({ channelId }) => channelId);
        const posDeviceIds = formPosDevices.map(({ posDeviceId }) => posDeviceId);
        const moduleMarketplaces = [...formMarketplaceStores];

        const canSyncWeb = !(formIsPosSyncActive && posDeviceIds.length > 0);
        if (!canSyncWeb && channelIds.length > 0 && !isLinkingMenuToPosAndWebEnabled) {
            setErrorMessageChannels(computeText(intl, 'menu.modal.error.channels'));
            return;
        }

        if (!name) {
            setError('name', { message: computeText(intl, 'menu.modal.error.wrongName') });
            return;
        }

        const oldHadKiosk = channels.some((c) => c.channelId === ChannelId.KIOSK);
        const newHasKiosk = channelIds.includes(ChannelId.KIOSK);
        const kioskChanged = menuId ? oldHadKiosk !== newHasKiosk : newHasKiosk;

        let activeBanners: PromotionalBanner[] = [];
        if (kioskChanged && restaurantId) {
            activeBanners = await getActiveBanners(restaurantId);
        }

        if (menuId) {
            await putMenuById({
                menuId,
                name,
                channels: channelIds,
                posDevices: posDeviceIds,
                isSyncFromPosEnabled: formIsPosSyncActive,
                moduleMarketplaces: formMarketplaceStores,
                externalId,
                token,
                imageId,
            });
            if (kioskChanged && activeBanners.length > 0) {
                await deactivateAndNotify(activeBanners, Number(restaurantId));
            }
            await refetchExternalIds();
            return;
        }
        if (!file) {
            await postMenu({
                name,
                brandId,
                sourcePosId,
                restaurantId,
                channels: channelIds,
                posDevices: posDeviceIds,
                isSyncFromPosEnabled: formIsPosSyncActive,
                token,
                imageId,
            });
            if (kioskChanged && activeBanners.length > 0) {
                await deactivateAndNotify(activeBanners, Number(restaurantId));
            }
            return;
        }

        const fileReader = new FileReader();
        let fileToUpdate: File | Record<string, unknown> = { ...file };

        if (file.type === 'application/json') {
            fileReader.onload = (e) => {
                if (e.target && typeof e.target.result === 'string') {
                    try {
                        fileToUpdate = JSON.parse(e.target.result) as Record<string, unknown>;
                    } catch (err) {
                        setError('file', { message: computeText(intl, 'file.cannotProcessJson') });
                        setCurrentStep(0);
                        return;
                    }

                    postMenu({
                        name,
                        brandId,
                        restaurantId,
                        channels: channelIds,
                        posDevices: posDeviceIds,
                        import: fileToUpdate,
                        isSyncFromPosEnabled: formIsPosSyncActive,
                        token,
                        imageId,
                    });
                }
            };
            fileReader.readAsText(file, 'UTF-8');
        } else {
            const formData = new FormData();
            formData.append('file', file);
            setImportProcessLoading(true);
            const importMenuParams = restaurantId ? { restaurantId } : { brandId };
            const importedMenu = (await importMenuFromExcel({
                name,
                formData,
                token,
                ...importMenuParams,
            })) as { data?: PostImportMenuFromExcelResponse };

            // We cannot assign channels with current import api so we update afterward
            if (importedMenu?.data?.menuId) {
                await putMenuById({
                    menuId: importedMenu?.data?.menuId,
                    name,
                    channels: channelIds,
                    posDevices: posDeviceIds,
                    isSyncFromPosEnabled: formIsPosSyncActive,
                    moduleMarketplaces,
                    token,
                    imageId,
                });
                if (kioskChanged && activeBanners.length > 0) {
                    await deactivateAndNotify(activeBanners, Number(restaurantId));
                }
            }
            setImportProcessLoading(false);
        }
    };

    useEffect(() => {
        if (!isLoading && isSuccess) {
            handleHide();
        }
    }, [isLoading, isSuccess, handleHide]);

    useEffect(() => {
        if (dataExternalIds && menuId) {
            form.setValue('externalId', menuId ? dataExternalIds[menuId] : '');
        }
    }, [dataExternalIds, menuId, form.setValue]);

    useEffect(() => {
        if (menu?.image) {
            form.setValue('imageId', menu.image.imageId.toString());
        }
    }, [menu, form.setValue, form]);

    return {
        form,
        currentStep,
        steps,
        errorMessage,
        errorMessageChannels,
        handleHide,
        isLoading,
        onFormValidate,
    };
};
